import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Mui from "@mui/material";
import { Box, IconButton } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { Description } from "@mui/icons-material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BadgeIcon from "@mui/icons-material/Badge";
import { useSelector } from "react-redux";
import moment from "moment";
import { pdf } from "@react-pdf/renderer";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { CompactHeaderSelectFilter } from "components/CustomComponents/CompactHeaderSelectFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { useExcelDownload } from "components/CustomComponents/useExcelDownload";
import { FilterProvider } from "pages/FilterContext";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import {
  theme,
  optionalAvathar,
  defaultAllSelection,
} from "services/constants";
import { showToast } from "services/utils/Status";
import BulkTimeCardData from "./BulkTimeCardPdfData";
import {
  APP_BAR_OFFSET,
  compactRowPager,
  designColors,
  designShell,
  paginationFooter,
  rowsPerPageOptions,
  withDesignFont,
} from "services/constants/designTokens";
import {
  SearchRounded,
  Groups2Rounded,
  ScheduleRounded,
  WatchLaterRounded,
  Functions,
  CheckCircleOutline,
} from "@mui/icons-material";
import {
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableBodyCell,
  approveTableHeadCell,
  ApproveTable,
} from "pages/TeamZone/Approve/approveUi";

const EXPAND_TOGGLE_OPTIONS = [
  { id: "collapse-all", label: "Collapse All" },
  { id: "expand-all", label: "Expand All" },
];
// Date-wise outer table is bounded to a date range's worth of rows rather
// than a whole employee roster, so a smaller page size than the app-wide
// default reads better.
const DATE_WISE_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
// Fixed page size for a single expanded date's employee list — a busy date
// can embed thousands of entries even though the outer table is paginated.
const DATE_WISE_DETAIL_PAGE_SIZE = 25;
const timeCardDateWiseHeaders = [
  {
    name: "Date",
    key: "date",
    type: "date",
    icon: <ScheduleRounded fontSize="small" />,
  },
  {
    name: "Working Hours",
    key: "total_working_hours",
    type: "time",
    icon: <AccessTimeIcon fontSize="small" />,
  },
];
const timeCardDateWiseDetailHeaders = [
  {
    name: "Employee Name",
    key: "employee_name",
    type: "string",
    align: "left",
  },
  { name: "Employee Id", key: "employee_id", type: "string", align: "left" },
  {
    name: "Department Name",
    key: "department_name",
    type: "autocomplete",
    align: "left",
  },
  { name: "Attendance Date", key: "attendance_date", type: "date" },
  { name: "IN Time", key: "in_time", type: "time" },
  { name: "OUT Time", key: "out_time", type: "time" },
  { name: "Day Total Hrs", key: "total_hours", type: "time" },
  { name: "Status", key: "status_display", type: "autocomplete" },
];

const timeCardSummaryHeaders = [
  {
    name: "Employee Name",
    key: "employee_name",
    type: "string",
    icon: <PersonOutlineIcon fontSize="small" />,
  },
  {
    name: "Employee Id",
    key: "employee_id",
    type: "string",
    icon: <BadgeIcon fontSize="small" />,
  },
  {
    name: "Department Name",
    key: "department_name",
    type: "autocomplete",
    icon: <Groups2Rounded fontSize="small" />,
  },
  {
    name: "Total Working Hours",
    key: "total_working_hours",
    type: "time",
    icon: <AccessTimeIcon fontSize="small" />,
  },
];

// Mirrors WorkingHours/index.js's workingHoursDetailTableContainerSx /
// workingHoursDetailTableSx byte-for-byte — this is the same nested
// per-employee drill-down pattern (its own bordered, rounded scroll box with
// a sticky slate-100 head), just local to this file since TimeCardReport is
// its own component and the two files don't import from each other.
const timeCardDetailTableContainerSx = {
  marginTop: 0,
  marginLeft: "auto",
  marginRight: "auto",
  position: "relative",
  width: "100%",
  maxHeight: 650,
  overflow: "auto",
  border: `1px solid ${designColors.slate200}`,
  borderRadius: 2,
  backgroundColor: designColors.white,
  // Rows become taller cards below 900px, so drop the fixed height cap.
  "@media (max-width:899.95px)": { maxHeight: "none", overflow: "visible" },
};

const timeCardDetailTableSx = {
  "& .MuiTableHead-root": {
    ...designShell.tableStickyHead,
    zIndex: 0,
  },
  "& .MuiTableHead-root .MuiTableRow-root": {
    ...designShell.tableStickyHeadRow,
    zIndex: 0,
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    ...approveTableHeadCell,
    top: 0,
    zIndex: 1,
    backgroundColor: designColors.slate100,
  },
  "& .MuiTableBody-root .MuiTableCell-root": approveTableBodyCell,
  "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": {
    borderBottom: "none",
  },
};

// Identity columns line up 1:1 with timeCardSummaryHeaders' keys (the ones a
// saved view can hide/reorder); the detail columns after them come from the
// per-employee day drill-down and have no on-screen equivalent, so they're
// always included.
const TIME_CARD_IDENTITY_EXPORT_COLUMNS = [
  { name: "Employee Name", key: "Employee Name", sourceKey: "employee_name" },
  { name: "Employee Id", key: "Employee Id", sourceKey: "employee_id" },
  {
    name: "Department Name",
    key: "Department Name",
    sourceKey: "department_name",
  },
  {
    name: "Total Working Hours",
    key: "Total Working Hours",
    sourceKey: "total_working_hours",
  },
];

const timeCardExpandHeaders = [
  {
    name: "Attendance Date",
    key: "attendance_date",
    type: "date",
    icon: <ScheduleRounded fontSize="small" />,
  },
  {
    name: "IN Time",
    key: "in_time",
    type: "time",
    icon: <AccessTimeIcon fontSize="small" />,
    lockGroup: "time_card_shift_time",
  },
  {
    name: "OUT Time",
    key: "out_time",
    type: "time",
    icon: <WatchLaterRounded fontSize="small" />,
    lockGroup: "time_card_shift_time",
  },
  {
    name: "Day Total Hrs",
    key: "total_hours",
    type: "time",
    icon: <Functions fontSize="small" />,
  },
  {
    name: "Status",
    key: "status_display",
    type: "autocomplete",
    icon: <CheckCircleOutline fontSize="small" />,
  },
];

const timeCardIdentityValueGetters = {
  employee_name: (row) => row?.employee_name || "-",
  employee_id: (row) => row?.employee_id || "-",
  department_name: (row) => row?.department_name || "-",
  total_working_hours: (row) => row?.total_working_hours || "00:00",
};

// Day Total Hrs values are display strings, not raw HH:MM — a working day
// reads "8:30 Hrs" while non-working days read "WO" / "NH/FH" / "-", so only
// the " Hrs"-suffixed values are real durations worth summing into the
// per-employee Total row; the rest are markers, not durations.
const sumTimeCardDurationValues = (values) => {
  const totalMinutes = (values || []).reduce((sum, value) => {
    const match = /^(\d{1,4}):(\d{2})\s*Hrs$/i.exec(`${value ?? ""}`.trim());
    if (!match) return sum;
    return sum + Number(match[1]) * 60 + Number(match[2]);
  }, 0);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}`;
};

const sumRawWorkingHours = (values = []) => {
  const minutes = values.reduce((sum, value) => {
    const match = /^(\d{1,4}):(\d{2})/.exec(`${value || ""}`.trim());
    return match ? sum + Number(match[1]) * 60 + Number(match[2]) : sum;
  }, 0);
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}`;
};

// Same counting logic as the aggregate "Total Present / Holiday / Absent /
// Weekly Off / Overtime" metadata shown on the report export — applied to a
// single employee's summary row instead of reduced across all of them.
const toCount = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const parseTimeToMinutesForOvertime = (value) => {
  const normalizedValue = `${value || ""}`.trim();
  if (!normalizedValue) return 0;
  const match = normalizedValue.match(/^(\d{1,3}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return 0;
  return (Number(match[1]) || 0) * 60 + (Number(match[2]) || 0);
};

const getEmployeeOvertimeCount = (row) =>
  toCount(row?.attendant_ot_count) ||
  toCount(row?.no_of_ot) ||
  toCount(row?.ot_count) ||
  (parseTimeToMinutesForOvertime(row?.ot_hours) > 0 ? 1 : 0);

const getTimeCardAttendanceCounts = (detailRows = []) => {
  const rowsByDate = new Map();

  detailRows.forEach((row, index) => {
    const status = `${row?.status || ""}`.trim().toUpperCase();
    const statusText = [
      row?.status,
      row?.status_label,
      row?.attendance_status,
      row?.type,
      row?.status_display,
      row?.total_hours,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const totalHours = `${row?.total_hours || ""}`.trim().toUpperCase();
    const isAbsent =
      ["A", "AB", "ABSENT"].includes(status) || statusText.includes("absent");
    const isMissing =
      ["M", "MIS", "MISSING"].includes(status) ||
      statusText.includes("missing") ||
      totalHours === "MISSING";

    const dateKey = row?.raw_date || row?.attendance_date || `row-${index}`;
    const existing = rowsByDate.get(dateKey);
    if (!existing || (isMissing && !existing.isMissing)) {
      rowsByDate.set(dateKey, { row, isAbsent, isMissing });
    }
  });

  return Array.from(rowsByDate.values()).reduce(
    (counts, { row, isAbsent, isMissing }) => {
      const status = `${row?.status || ""}`.trim().toUpperCase();
      const display =
        `${row?.status_display || row?.status_label || row?.type || row?.status || ""}`
          .trim()
          .toLowerCase();
      const marker = `${row?.in_time || ""}`.trim().toUpperCase();
      const hasWorked =
        /^\d{1,4}:\d{2}\s*HRS$/i.test(`${row?.total_hours || ""}`) ||
        (Boolean(row?.working_hours) && row.working_hours !== "00:00") ||
        Boolean(
          row?.check_in_time_formatted &&
          row.check_in_time_formatted !== "null",
        ) ||
        (Boolean(row?.in_time) &&
          !["-", "WO", "NH", "FH", "NH / FH"].includes(marker));
      const isOvertime =
        status === "OT" ||
        display === "overtime" ||
        display.includes("overtime") ||
        display.includes("(ot)") ||
        toCount(row?.ot_count) > 0 ||
        toCount(row?.no_of_ot) > 0 ||
        parseTimeToMinutesForOvertime(row?.ot_hours) > 0;

      if (isAbsent) {
        counts.absent += 1;
      } else if (isMissing) {
        // The business report treats Missing as present for this summary.
        counts.present += 1;
      } else if (
        status === "WO" ||
        status === "W/O" ||
        marker === "WO" ||
        display.includes("week off") ||
        display.includes("weekly off")
      ) {
        counts.weekOff += 1;
      } else if (
        ["NH", "FH", "NHFH", "NH / FH", "H"].includes(status) ||
        marker === "NH" ||
        marker === "FH" ||
        display.includes("holiday") ||
        display.includes("national") ||
        display.includes("festival")
      ) {
        counts.holiday += 1;
      } else if (
        hasWorked ||
        ["P", "PL", "LP", "PR"].includes(status) ||
        display === "present" ||
        display.includes("late") ||
        display.includes("present") ||
        (isOvertime &&
          (row?.working_hours || row?.check_in_time_formatted || row?.in_time))
      ) {
        counts.present += 1;
      }

      return counts;
    },
    { present: 0, absent: 0, holiday: 0, weekOff: 0 },
  );
};

// multiple_individual_working_hours returns designation_name/date_of_join
// directly on each employee record (confirmed from a live response) — those
// take priority over the guessed keys from the list/employee fallback.
const getEmployeeDesignation = (profile) =>
  profile?.designation_name ||
  profile?.designation ||
  profile?.user_designation ||
  "-";

const getEmployeeDateOfJoin = (profile) =>
  profile?.date_of_join ||
  profile?.date_of_joining ||
  profile?.doj ||
  profile?.joining_date ||
  "-";

const getEmployeeLunchHours = (profile) =>
  profile?.shift_break_hours ||
  profile?.lunch_hours ||
  profile?.break_hours ||
  "-";

export const TimeCardReport = ({
  data,
  page,
  setLeavePage,
  rowsPerPage,
  setLeaveRowsPerPage,
  metadata,
  selectedEmployee,
  screenAccess,
  subModuleId,
  startDate,
  endDate,
  viewMode,
}) => {
  const { domain, token, userDetails, topbarCompanyName, topbarCompanyId } =
    useSelector((state) => state.tokenReducer);
  const {
    downloadExcel,
    downloadPdf,
    downloadCsv,
    downloadGroupedExcel,
    downloadGroupedPdf,
    downloadGroupedCsv,
  } = useExcelDownload();
  const { departmentId } = useSelector((state) => state.userReducer);
  const [filteredRows, setFilteredRows] = useState(null);
  // Tracks which activeBaseRows filteredRows was actually computed from, so a
  // fresh date range / view mode swap doesn't render one frame of the
  // previous filter result (or an empty table) before CustomFilter's own
  // effect catches up — same pattern as Administration/ListView.js.
  const filteredRowsSourceRef = useRef(null);
  const [expandedEmployeeId, setExpandedEmployeeId] = useState(null);
  const [expandMode, setExpandMode] = useState("collapse-all");
  const [expandedEmployeeIds, setExpandedEmployeeIds] = useState([]);
  const [expandedRowsByEmployee, setExpandedRowsByEmployee] = useState({});
  const [loadingEmployeeIds, setLoadingEmployeeIds] = useState([]);
  const [dateDetailsByKey, setDateDetailsByKey] = useState({});
  const [loadingDateKeys, setLoadingDateKeys] = useState([]);
  const [expandedDateKeys, setExpandedDateKeys] = useState([]);
  // A single expanded date can hold thousands of employee rows even though
  // the outer date-wise table is paginated — these page and search that
  // inner drill-down table independently, same pattern as Break Report.
  const [dateWiseDetailPageByKey, setDateWiseDetailPageByKey] = useState({});
  const [dateWiseDetailSearchByKey, setDateWiseDetailSearchByKey] = useState(
    {},
  );
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const footerRef = useCallback((node) => setFooterNode(node), []);
  const shouldShowDepartmentColumn =
    `${departmentId}` === `${defaultAllSelection}`;
  const timeCardHeaders = useMemo(() => {
    if (shouldShowDepartmentColumn) {
      return timeCardSummaryHeaders;
    }

    return timeCardSummaryHeaders.filter(
      (header) => header.key !== "department_name",
    );
  }, [shouldShowDepartmentColumn]);
  const mainHeaderKeys = useMemo(
    () => new Set(timeCardHeaders.map((header) => header.key)),
    [timeCardHeaders],
  );
  const expandHeaderOrder = useMemo(
    () =>
      new Map(
        timeCardExpandHeaders.map((header, index) => [header.key, index]),
      ),
    [],
  );
  const combinedFullHeaders = useMemo(
    () => [
      ...timeCardHeaders.map((header) => ({
        ...header,
        groupName: "Main Table",
      })),
      ...timeCardExpandHeaders.map((header) => ({
        ...header,
        groupName: "Expand Panel",
      })),
    ],
    [timeCardHeaders],
  );

  const handleSavedViewChange = useCallback(() => {
    setLeavePage(0);
  }, [setLeavePage]);

  const { visibleHeaders: combinedVisibleHeaders, titleOverride } =
    useSavedViewSelector({
      domain,
      userId: userDetails?.id,
      subModuleId,
      storageKey: "time_card_report_saved_views",
      fullHeaders: combinedFullHeaders,
      screenAccess,
      onViewChange: handleSavedViewChange,
    });
  const visibleHeaders = useMemo(
    () =>
      combinedVisibleHeaders.filter((header) => mainHeaderKeys.has(header.key)),
    [combinedVisibleHeaders, mainHeaderKeys],
  );
  const expandVisibleHeaders = useMemo(() => {
    const expandHeaders = combinedVisibleHeaders.filter(
      (header) => !mainHeaderKeys.has(header.key),
    );
    const inTimeIndex = expandHeaders.findIndex(
      (header) => header.key === "in_time",
    );
    const outTimeIndex = expandHeaders.findIndex(
      (header) => header.key === "out_time",
    );

    if (inTimeIndex === -1 || outTimeIndex === -1) {
      return expandHeaders;
    }

    const nextHeaders = [...expandHeaders];
    const pairStartIndex = Math.min(inTimeIndex, outTimeIndex);
    const pairHeaders = [
      nextHeaders[inTimeIndex],
      nextHeaders[outTimeIndex],
    ].sort(
      (left, right) =>
        (expandHeaderOrder.get(left.key) ?? 0) -
        (expandHeaderOrder.get(right.key) ?? 0),
    );

    nextHeaders.splice(pairStartIndex, 2, ...pairHeaders);

    return nextHeaders;
  }, [combinedVisibleHeaders, expandHeaderOrder, mainHeaderKeys]);

  useEffect(() => {
    if (!customFilterNode) return undefined;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode, visibleHeaders.length]);

  useEffect(() => {
    if (!footerNode) return undefined;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  const baseRows = useMemo(() => {
    const employeeFilterId = `${selectedEmployee?.user_id || ""}`;

    return (data || [])
      .filter((row) => row?.employee_name || row?.employee_id)
      .filter((row) => {
        if (!employeeFilterId || employeeFilterId === String(defaultAllSelection)) {
          return true;
        }

        return (
          `${row?.user_id || ""}` === employeeFilterId ||
          `${row?.id || ""}` === employeeFilterId ||
          `${row?.employee_id || ""}` ===
            `${selectedEmployee?.employee_id || ""}`
        );
      });
  }, [data, selectedEmployee?.employee_id, selectedEmployee?.user_id]);

  const isDateWiseView = viewMode?.id === "date-wise";
  // Groups every employee's daily detail by normalized date, once, in a
  // single O(employees x details) pass. dateWiseRows below and
  // ensureExpandedRowsForDate used to redo this same flatMap+moment-parse
  // scan of the *entire* employee list independently — dateWiseRows once
  // per date in the range, and ensureExpandedRowsForDate once per date
  // every time a row was expanded. With thousands of employees loaded,
  // that repeated full scan is what made both slow.
  const dateWiseDetailsByDate = useMemo(() => {
    const map = new Map();
    (Array.isArray(baseRows) ? baseRows : []).forEach((employee) => {
      const details = Array.isArray(employee?.user_details)
        ? employee.user_details
        : Array.isArray(employee?.checkin_details)
          ? employee.checkin_details
          : employee?.days && typeof employee.days === "object"
            ? Object.values(employee.days)
            : [];
      details.forEach((detail) => {
        const normalizedDate = moment(
          detail?.date || detail?.attendance_date,
          ["YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601],
          true,
        ).format("YYYY-MM-DD");
        const mergedDetail = {
          ...detail,
          employee_name:
            employee?.employee_name ||
            employee?.username ||
            detail?.user_name ||
            "-",
          employee_id: employee?.employee_id || detail?.employee_id || "-",
          department_name:
            employee?.department_name || detail?.department_name || "-",
        };
        const existing = map.get(normalizedDate);
        if (existing) {
          existing.push(mergedDetail);
        } else {
          map.set(normalizedDate, [mergedDetail]);
        }
      });
    });
    return map;
  }, [baseRows]);
  const dateWiseRows = useMemo(() => {
    const firstDate = moment(startDate, "YYYY-MM-DD", true);
    const lastDate = moment(endDate, "YYYY-MM-DD", true);
    if (
      !firstDate.isValid() ||
      !lastDate.isValid() ||
      lastDate.isBefore(firstDate)
    ) {
      return [];
    }

    const rows = [];
    const currentDate = firstDate.clone();
    while (currentDate.isSameOrBefore(lastDate, "day")) {
      const dateKey = currentDate.format("YYYY-MM-DD");
      const dateDetails = dateWiseDetailsByDate.get(dateKey) || [];
      rows.push({
        id: dateKey,
        date_key: dateKey,
        // CustomFilter compares actual dates. Keep this value normalized and
        // format it only at render time so the Date Wise filter can match it.
        date: dateKey,
        employee_count: dateDetails.length,
        total_working_hours: sumRawWorkingHours(
          dateDetails.map((detail) => detail?.working_hours),
        ),
      });
      currentDate.add(1, "day");
    }
    return rows;
  }, [dateWiseDetailsByDate, endDate, startDate]);
  const activeHeaders = isDateWiseView
    ? timeCardDateWiseHeaders
    : visibleHeaders;
  const activeBaseRows = isDateWiseView ? dateWiseRows : baseRows;
  const filteredRowsReady = filteredRowsSourceRef.current === activeBaseRows;
  const visibleRows =
    filteredRows !== null && filteredRowsReady ? filteredRows : activeBaseRows;

  const getSummarizedDayRowCount = useCallback((row) => {
    if (!row || typeof row !== "object") return 0;

    const countKeys = [
      "present_count",
      "absent_count",
      "leave_count",
      "holiday_count",
      "week_off_count",
    ];

    const total = countKeys.reduce(
      (sum, key) => sum + (Number(row?.[key]) || 0),
      0,
    );

    return total > 0 ? total : 1;
  }, []);

  const timeCardPdfRowCount = useMemo(
    () =>
      (visibleRows || []).reduce(
        (sum, row) => sum + getSummarizedDayRowCount(row),
        0,
      ),
    [visibleRows, getSummarizedDayRowCount],
  );

  useEffect(() => {
    setLeavePage(0);
    setExpandMode("collapse-all");
    setExpandedEmployeeIds([]);
    setExpandedEmployeeId(null);
    setExpandedDateKeys([]);
    setDateWiseDetailPageByKey({});
    setDateWiseDetailSearchByKey({});
  }, [activeBaseRows, setLeavePage]);

  const handleChangeRowsPerPage = (event) => {
    setLeaveRowsPerPage(parseInt(event.target.value, 10));
    setLeavePage(0);
  };

  const handleFilteredDataChange = useCallback(
    (nextRows) => {
      filteredRowsSourceRef.current = activeBaseRows;
      setFilteredRows(nextRows);
      setLeavePage(0);
    },
    [activeBaseRows, setLeavePage],
  );

  function formatAttendanceDate(dateValue) {
    const parsedDate = moment(
      dateValue,
      ["DD-MM-YYYY", "YYYY-MM-DD", moment.ISO_8601],
      true,
    );

    if (!parsedDate.isValid()) {
      return dateValue || "-";
    }

    return parsedDate.format("DD/MM/YYYY ddd");
  }

  const formatClockTime = (value) => {
    if (!value || value === "null") return "-";
    const parsedTime = moment(value, ["HH:mm", "HH:mm:ss", "hh:mm A"], true);
    return parsedTime.isValid() ? parsedTime.format("hh:mm A") : value;
  };

  const getTimeCardMarker = (item) => {
    const normalizedStatus = `${item?.status || ""}`.toUpperCase();
    const normalizedLabel = `${item?.status_label || ""}`.toLowerCase();
    const normalizedType = `${item?.type || ""}`.toLowerCase();

    if (
      normalizedStatus === "WO" ||
      normalizedLabel.includes("week") ||
      normalizedType.includes("week")
    ) {
      return "WO";
    }

    if (normalizedStatus === "NH" || normalizedLabel.includes("national")) {
      return "NH";
    }

    if (normalizedStatus === "FH" || normalizedLabel.includes("festival")) {
      return "FH";
    }

    if (
      normalizedStatus === "NHFH" ||
      normalizedLabel.includes("holiday") ||
      normalizedType.includes("holiday")
    ) {
      // Holiday, but the API didn't say which kind — no NH/FH split possible.
      return "NH / FH";
    }

    return "-";
  };

  const getStatusDisplay = (item) => {
    const baseStatus =
      item?.status_label ||
      item?.type ||
      item?.attendance_status ||
      item?.status ||
      "-";

    const hasOvertime =
      toCount(item?.attendant_ot_count) ||
      toCount(item?.no_of_ot) ||
      toCount(item?.ot_count) ||
      parseTimeToMinutesForOvertime(item?.ot_hours) > 0;

    return hasOvertime ? `${baseStatus} (OT)` : baseStatus;
  };

  const buildTimeCardRows = useCallback(
    (rawRows = []) =>
      [...rawRows]
        .sort((left, right) => {
          const leftDate = moment(
            left?.date,
            ["YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601],
            true,
          );
          const rightDate = moment(
            right?.date,
            ["YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601],
            true,
          );

          if (leftDate.isValid() && rightDate.isValid()) {
            return leftDate.valueOf() - rightDate.valueOf();
          }

          if (leftDate.isValid()) return -1;
          if (rightDate.isValid()) return 1;
          return 0;
        })
        .map((item, index) => {
          const marker = getTimeCardMarker(item);
          const hasCheckIn =
            item?.check_in_time_formatted &&
            item.check_in_time_formatted !== "null";
          const hasCheckOut =
            item?.check_out_time_formatted &&
            item.check_out_time_formatted !== "null";

          return {
            id: item?.id || index,
            raw_date: item?.date,
            status: item?.status,
            status_label: item?.status_label,
            type: item?.type,
            status_color: item?.color || "#6D7483",
            attendance_date: formatAttendanceDate(item?.date),
            in_time: hasCheckIn
              ? formatClockTime(item?.check_in_time_formatted)
              : marker,
            out_time: hasCheckOut
              ? formatClockTime(item?.check_out_time_formatted)
              : "-",
            total_hours:
              item?.working_hours && item.working_hours !== "00:00"
                ? `${item.working_hours} Hrs`
                : marker !== "-"
                  ? marker
                  : item?.working_hours || "-",
            status_display: getStatusDisplay(item),
          };
        }),
    [],
  );

  const handleSummarizedDownload = useCallback(
    async (
      summaryRows,
      _metadataOverride,
      _filterValues,
      _sortRules,
      downloadFormat = "excel",
      onProgress,
    ) => {
      const rows = Array.isArray(summaryRows) ? summaryRows : [];
      if (!rows.length) {
        showToast("info", "No time card records are available for export.");
        return;
      }

      const exportToFormat =
        downloadFormat === "pdf"
          ? downloadPdf
          : downloadFormat === "csv"
            ? downloadCsv
            : downloadExcel;

      await exportToFormat(
        rows,
        activeHeaders,
        "Time Card",
        {},
        { ...metadata, "Total User Count": rows.length },
        [],
        onProgress,
      );
    },
    [activeHeaders, downloadCsv, downloadExcel, downloadPdf, metadata],
  );

  // Fetches every day-level attendance row for the given summary rows, paging
  // through the API and grouping the results by employee. Shared by the
  // Excel/PDF/CSV export and the one-page-per-employee bulk PDF, so both stay
  // in sync with the same source data instead of each re-implementing the
  // pagination.
  const fetchDetailRowsByEmployee = useCallback(
    async (summaryRows, onProgress) => {
      const employeeMap = new Map(
        summaryRows.flatMap((item) => {
          const entries = [];
          if (item?.user_id) entries.push([`${item.user_id}`, item]);
          if (item?.employee_id) entries.push([`${item.employee_id}`, item]);
          return entries;
        }),
      );

      const getEmployeeRecord = (detailItem) => {
        const lookupKeys = [
          detailItem?.user_id,
          detailItem?.employee_id,
          detailItem?.user?.id,
          detailItem?.employee?.id,
        ]
          .filter(Boolean)
          .map((value) => `${value}`);

        for (const key of lookupKeys) {
          if (employeeMap.has(key)) return employeeMap.get(key);
        }

        return null;
      };

      const extractPageItems = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.results)) return payload.results;
        if (Array.isArray(payload?.data)) return payload.data;
        if (Array.isArray(payload?.records)) return payload.records;
        if (Array.isArray(payload?.items)) return payload.items;
        if (Array.isArray(payload?.checkin_details))
          return payload.checkin_details;
        return [];
      };

      const detailRowsByEmployee = new Map();
      const profileByEmployee = new Map();
      let currentPage = 1;
      let totalPages = 1;

      if (typeof onProgress === "function") {
        onProgress(0);
      }

      while (currentPage <= totalPages) {
        const params = new URLSearchParams({
          user_id: `${userDetails?.id}`,
          department_id: `${departmentId}`,
          start_date: startDate,
          end_date: endDate,
          page: `${currentPage}`,
        });

        const response = await fetch(
          `${domain}multiple_individual_working_hours/?${params.toString()}&company_id=${topbarCompanyId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );

        const res = await response.json();

        if (!response.ok) {
          throw new Error(res?.error || "Unable to fetch time card report");
        }

        const pageItems = extractPageItems(res);

        pageItems.forEach((item) => {
          const employeeRecord = getEmployeeRecord(item);
          if (!employeeRecord) return;

          const employeeKey = `${employeeRecord?.user_id || employeeRecord?.employee_id}`;

          if (
            item?.designation_name ||
            item?.date_of_join ||
            item?.shift_break_hours
          ) {
            profileByEmployee.set(employeeKey, {
              designation_name: item?.designation_name,
              date_of_join: item?.date_of_join,
              shift_break_hours: item?.shift_break_hours,
            });
          }

          const sourceRows = Array.isArray(item?.checkin_details)
            ? item.checkin_details
            : item?.days && typeof item.days === "object"
              ? Object.values(item.days)
              : [item];

          const mappedRows = buildTimeCardRows(
            sourceRows.map((detail) => ({
              ...detail,
              user_id: detail?.user_id || item?.user_id,
              employee_id: detail?.employee_id || item?.employee_id,
            })),
          );

          const existingRows = detailRowsByEmployee.get(employeeKey) || [];
          detailRowsByEmployee.set(employeeKey, [
            ...existingRows,
            ...mappedRows,
          ]);
        });

        const pagination = res?.pagination || {};
        const resolvedTotalPages = Number(pagination.total_pages);
        totalPages =
          Number.isFinite(resolvedTotalPages) && resolvedTotalPages > 0
            ? resolvedTotalPages
            : totalPages;

        if (typeof onProgress === "function") {
          const progressValue = Math.min(
            100,
            Math.round((currentPage / totalPages) * 100),
          );
          onProgress(progressValue);
        }

        if (
          pagination.next_page === null ||
          pagination.next_page === undefined
        ) {
          break;
        }

        currentPage = Number(pagination.next_page);
        if (!Number.isFinite(currentPage) || currentPage <= 0) {
          break;
        }
      }

      if (typeof onProgress === "function") {
        onProgress(100);
      }

      return { detailRowsByEmployee, profileByEmployee };
    },
    [
      buildTimeCardRows,
      departmentId,
      domain,
      endDate,
      startDate,
      token,
      userDetails?.id, topbarCompanyId
    ],
  );

  const handleDownloadReport = useCallback(
    async (
      summaryRows,
      _metadataOverride,
      _filterValues,
      _sortRules,
      downloadFormat = "excel",
      onProgress,
    ) => {
      if (!summaryRows?.length) {
        showToast("info", "No time card records are available for export.");
        return;
      }

      try {
        const { detailRowsByEmployee } = await fetchDetailRowsByEmployee(
          summaryRows,
          onProgress,
        );

        // The saved view's own key order (not
        // TIME_CARD_IDENTITY_EXPORT_COLUMNS' fixed order) drives both which
        // identity columns are exported and in what order, so hiding/
        // reordering columns on screen carries through to the download too.
        const visibleIdentityColumns = visibleHeaders
          .map((header) =>
            TIME_CARD_IDENTITY_EXPORT_COLUMNS.find(
              (column) => column.sourceKey === header.key,
            ),
          )
          .filter(Boolean);

        const groups = summaryRows.map((row) => {
          const employeeKey = `${row?.user_id || row?.employee_id}`;
          const summaryFields = {};
          visibleIdentityColumns.forEach((column) => {
            summaryFields[column.sourceKey] =
              timeCardIdentityValueGetters[column.sourceKey](row);
          });
          const detailRows = detailRowsByEmployee.get(employeeKey) || [];

          return {
            summaryRow: summaryFields,
            detailRows,
            totals: {
              total_hours: sumTimeCardDurationValues(
                detailRows.map((detailRow) => detailRow?.total_hours),
              ),
            },
          };
        });

        const exportToFormat =
          downloadFormat === "pdf"
            ? downloadGroupedPdf
            : downloadFormat === "csv"
              ? downloadGroupedCsv
              : downloadGroupedExcel;

        await exportToFormat(
          groups,
          visibleHeaders,
          expandVisibleHeaders,
          "Time Card",
          { ...metadata, "Total User Count": summaryRows.length },
        );

        if (typeof onProgress === "function") {
          onProgress(100);
        }
      } catch (error) {
        showToast(
          "error",
          error.message || "Unable to download time card report",
        );
      }
    },
    [
      downloadGroupedCsv,
      downloadGroupedExcel,
      downloadGroupedPdf,
      fetchDetailRowsByEmployee,
      metadata,
      expandVisibleHeaders,
      visibleHeaders,
    ],
  );

  // Mirrors Payroll's "Generate Bulk Payslip": one combined PDF with a
  // dedicated page per employee, instead of the flat multi-employee table
  // the Excel/PDF/CSV export above produces. Employee-wise view only — the
  // date-wise view's rows aren't shaped per-employee, so the button is
  // disabled there (see bulkTimeCardRows below).
  const [isGeneratingBulkTimeCard, setIsGeneratingBulkTimeCard] =
    useState(false);
  const [bulkTimeCardProgress, setBulkTimeCardProgress] = useState(0);
  const bulkTimeCardRows = useMemo(
    () => (isDateWiseView ? [] : visibleRows),
    [isDateWiseView, visibleRows],
  );

  const fetchCompanyAddress = useCallback(async () => {
    if (!topbarCompanyId) return "";
    try {
      const response = await fetch(`${domain}company/${topbarCompanyId}/`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
      });
      if (!response.ok) return "";
      const res = await response.json();
      return res?.address || res?.company_address || "";
    } catch {
      return "";
    }
  }, [domain, token, topbarCompanyId]);

  // A one-time, company-wide employee list (same endpoint the Employee
  // filter already uses) so each PDF page can show designation and date of
  // join without a per-employee call.
  const fetchEmployeeProfiles = useCallback(async () => {
    const profileMap = new Map();
    if (!topbarCompanyId) return profileMap;

    // The response is paginated ({ pagination, results }), not a bare array
    // — page through all of it so every employee in the bulk PDF resolves,
    // not just whoever happened to be on page 1.
    const extractPageItems = (payload) => {
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.results)) return payload.results;
      if (Array.isArray(payload?.data)) return payload.data;
      if (Array.isArray(payload?.records)) return payload.records;
      return [];
    };

    try {
      let currentPage = 1;
      let totalPages = 1;

      while (currentPage <= totalPages) {
        const response = await fetch(
          `${domain}list/employee?company_id=${topbarCompanyId}&department_id=${defaultAllSelection}&status=Active&page=${currentPage}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        if (!response.ok) break;
        const res = await response.json();

        extractPageItems(res).forEach((profile) => {
          if (profile?.user_id) profileMap.set(`${profile.user_id}`, profile);
          if (profile?.employee_id)
            profileMap.set(`${profile.employee_id}`, profile);
        });

        const pagination = res?.pagination || {};
        const resolvedTotalPages = Number(pagination.total_pages);
        totalPages =
          Number.isFinite(resolvedTotalPages) && resolvedTotalPages > 0
            ? resolvedTotalPages
            : totalPages;

        if (
          pagination.next_page === null ||
          pagination.next_page === undefined
        ) {
          break;
        }

        currentPage = Number(pagination.next_page);
        if (!Number.isFinite(currentPage) || currentPage <= 0) break;
      }

      return profileMap;
    } catch {
      return profileMap;
    }
  }, [domain, token, topbarCompanyId]);

  const generateBulkTimeCardPdf = useCallback(async () => {
    if (!bulkTimeCardRows?.length) {
      showToast("error", "No employee data available to generate time cards");
      return;
    }

    setIsGeneratingBulkTimeCard(true);
    setBulkTimeCardProgress(0);

    try {
      // Fetching each employee's daily rows is the slow part (paginated,
      // scales with employee count) — report that progress on the button
      // itself so it's not just a spinner with no sense of how long is left.
      // The company address and employee profiles (designation, date of
      // join) aren't part of the time card data itself, so they're fetched
      // alongside it, once, rather than per employee.
      const [
        { detailRowsByEmployee, profileByEmployee },
        companyAddress,
        employeeProfileByKey,
      ] = await Promise.all([
        fetchDetailRowsByEmployee(bulkTimeCardRows, setBulkTimeCardProgress),
        fetchCompanyAddress(),
        fetchEmployeeProfiles(),
      ]);

      const employeesPdfData = bulkTimeCardRows.map((row) => {
        const employeeKey = `${row?.user_id || row?.employee_id}`;
        const detailRows = detailRowsByEmployee.get(employeeKey) || [];
        const attendanceCounts = getTimeCardAttendanceCounts(detailRows);
        // profileByEmployee (from multiple_individual_working_hours) has
        // confirmed field names, so it wins over the list/employee fallback.
        const profile = {
          ...employeeProfileByKey.get(employeeKey),
          ...profileByEmployee.get(employeeKey),
        };
        return {
          employee_name: timeCardIdentityValueGetters.employee_name(row),
          employee_id: timeCardIdentityValueGetters.employee_id(row),
          department_name: timeCardIdentityValueGetters.department_name(row),
          total_working_hours:
            timeCardIdentityValueGetters.total_working_hours(row),
          designation: getEmployeeDesignation(profile),
          date_of_join: getEmployeeDateOfJoin(profile),
          lunch_hours: getEmployeeLunchHours(profile),
          present_count: attendanceCounts.present,
          holiday_count: attendanceCounts.holiday,
          absent_count: attendanceCounts.absent,
          week_off_count: attendanceCounts.weekOff,
          overtime_count: getEmployeeOvertimeCount(row),
          detailRows,
        };
      });

      const blob = await pdf(
        <BulkTimeCardData
          employeesPdfData={employeesPdfData}
          companyAddress={companyAddress}
          dateRange={`${moment(startDate).format("DD-MM-YYYY")} to ${moment(
            endDate,
          ).format("DD-MM-YYYY")}`}
          companyName={topbarCompanyName}
          reportGeneratedOn={moment().format("DD/MM/YYYY HH:mm:ss")}
        />,
      ).toBlob();

      if (!blob) {
        showToast("error", "Failed to generate PDF file");
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Time Card Report.pdf";

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error(error);
      showToast("error", `Failed to generate PDF: ${error?.message}`);
    } finally {
      setIsGeneratingBulkTimeCard(false);
      setBulkTimeCardProgress(0);
    }
  }, [
    bulkTimeCardRows,
    endDate,
    fetchCompanyAddress,
    fetchDetailRowsByEmployee,
    fetchEmployeeProfiles,
    startDate,
    topbarCompanyName,
  ]);

  const bulkTimeCardAction = (
    <Mui.Tooltip
      title={
        isGeneratingBulkTimeCard
          ? `Generating time cards… ${Math.round(bulkTimeCardProgress)}%`
          : isDateWiseView
            ? "Switch to Employee Wise view to generate a bulk time card"
            : "Generate Bulk Time Card (one page per employee)"
      }
      arrow
      placement="top"
    >
      <span>
        <IconButton
          onClick={generateBulkTimeCardPdf}
          disabled={isGeneratingBulkTimeCard || bulkTimeCardRows.length === 0}
          sx={{
            position: "relative",
            width: 40,
            height: 40,
            border: "1px solid",
            borderColor: isGeneratingBulkTimeCard ? "transparent" : "divider",
            color: "text.secondary",
          }}
        >
          {isGeneratingBulkTimeCard ? (
            <Mui.Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <Mui.CircularProgress
                variant="determinate"
                value={100}
                size={34}
                thickness={4}
                sx={{ position: "absolute", color: "#d9e7f7" }}
              />
              <Mui.CircularProgress
                variant="determinate"
                value={Math.max(bulkTimeCardProgress, 0)}
                size={34}
                thickness={4}
                sx={{ position: "absolute", color: "#1976d2" }}
              />
              <Mui.Typography
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  fontSize: "10px",
                  fontWeight: 800,
                  lineHeight: 1,
                  color: "#1976d2",
                  whiteSpace: "nowrap",
                  pointerEvents: "none",
                }}
              >
                {Math.round(bulkTimeCardProgress)}
              </Mui.Typography>
            </Mui.Box>
          ) : (
            <Description />
          )}
        </IconButton>
      </span>
    </Mui.Tooltip>
  );

  const ensureExpandedRowsForEmployee = useCallback(
    async (employeeId) => {
      if (!employeeId) return;
      if (expandedRowsByEmployee[employeeId] !== undefined) return;
      if (loadingEmployeeIds.includes(employeeId)) return;

      const embeddedEmployee = (Array.isArray(data) ? data : []).find(
        (row) => `${row?.user_id || row?.id}` === `${employeeId}`,
      );
      if (embeddedEmployee) {
        const embeddedRows = Array.isArray(embeddedEmployee?.user_details)
          ? embeddedEmployee.user_details
          : [];
        setExpandedRowsByEmployee((prev) => ({
          ...prev,
          [employeeId]: buildTimeCardRows(embeddedRows),
        }));
        return;
      }

      setLoadingEmployeeIds((prev) =>
        prev.includes(employeeId) ? prev : [...prev, employeeId],
      );

      try {
        const response = await fetch(
          `${domain}individual_details_working_hours/?user_id=${employeeId}&start_date=${startDate}&end_date=${endDate}&company_id=${topbarCompanyId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        const rawRows = Array.isArray(res)
          ? res
          : Array.isArray(res?.checkin_details)
            ? res.checkin_details
            : [];

        if (!response.ok) {
          throw new Error("REQUEST_FAILED");
        }

        setExpandedRowsByEmployee((prev) => ({
          ...prev,
          [employeeId]: buildTimeCardRows(rawRows),
        }));
      } catch (error) {
        console.error("[TimeCardReport] operation failed:", error);
        setExpandedRowsByEmployee((prev) => ({
          ...prev,
          [employeeId]: [],
        }));
        showToast(
          "error",
          "Couldn't load the time card details. Please try again.",
        );
      } finally {
        setLoadingEmployeeIds((prev) =>
          prev.filter((id) => `${id}` !== `${employeeId}`),
        );
      }
    },
    [
      buildTimeCardRows,
      data,
      domain,
      endDate,
      expandedRowsByEmployee,
      loadingEmployeeIds,
      startDate,
      token, topbarCompanyId
    ],
  );

  const ensureExpandedRowsForDate = useCallback(
    async (dateKey) => {
      if (!dateKey || dateDetailsByKey[dateKey] !== undefined) {
        return dateDetailsByKey[dateKey] || [];
      }
      if (loadingDateKeys.includes(dateKey)) return [];

      if (Array.isArray(data)) {
        // Reuses the same normalized date -> details map dateWiseRows uses,
        // instead of re-scanning every employee with its own flatMap once
        // per date being expanded — with thousands of employees loaded,
        // that redone O(employees) scan on every click was slow. Sourced
        // from baseRows (the employee-filtered set) so the drill-down
        // always matches the count dateWiseRows already showed.
        const embeddedRows = dateWiseDetailsByDate.get(dateKey) || [];
        const mappedRows = buildTimeCardRows(embeddedRows).map(
          (row, index) => ({
            ...row,
            employee_name: embeddedRows[index]?.employee_name || "-",
            employee_id: embeddedRows[index]?.employee_id || "-",
            department_name: embeddedRows[index]?.department_name || "-",
          }),
        );
        setDateDetailsByKey((prev) => ({ ...prev, [dateKey]: mappedRows }));
        return mappedRows;
      }

      setLoadingDateKeys((prev) => [...prev, dateKey]);
      try {
        const rows = [];
        let currentPage = 1;
        let nextPage = 1;

        while (nextPage) {
          const response = await fetch(
            `${domain}individual_details_working_hours_by_date/?user_id=${userDetails?.id}&date=${dateKey}&page=${currentPage}&company_id=${topbarCompanyId}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `token ${token}`,
              },
            },
          );
          const payload = await response.json();
          if (!response.ok) throw new Error("REQUEST_FAILED");

          const results = Array.isArray(payload)
            ? payload
            : payload?.results || payload?.data || [];
          results.forEach((item) => {
            const day =
              item?.days && typeof item.days === "object" ? item.days : item;
            rows.push({
              ...item,
              ...day,
              employee_name: item?.employee_name || item?.user_name || "-",
              employee_id: item?.employee_id || "-",
              department_name: item?.department_name || "-",
              date: day?.date || item?.date || dateKey,
            });
          });

          nextPage = payload?.pagination?.next_page;
          currentPage = Number(nextPage);
          if (!Number.isFinite(currentPage) || currentPage <= 0)
            nextPage = null;
        }

        const mappedRows = buildTimeCardRows(rows).map((row, index) => ({
          ...row,
          employee_name: rows[index]?.employee_name || "-",
          employee_id: rows[index]?.employee_id || "-",
          department_name: rows[index]?.department_name || "-",
        }));
        setDateDetailsByKey((prev) => ({ ...prev, [dateKey]: mappedRows }));
        return mappedRows;
      } catch (error) {
        console.error("[TimeCardReport] date detail request failed:", error);
        setDateDetailsByKey((prev) => ({ ...prev, [dateKey]: [] }));
        showToast(
          "error",
          "Couldn't load the time card details. Please try again.",
        );
        return [];
      } finally {
        setLoadingDateKeys((prev) => prev.filter((key) => key !== dateKey));
      }
    },
    [
      buildTimeCardRows,
      data,
      dateDetailsByKey,
      dateWiseDetailsByDate,
      domain,
      loadingDateKeys,
      token,
      userDetails?.id, topbarCompanyId
    ],
  );

  const handleDateWiseDetailedDownload = useCallback(
    async (
      dateRows,
      _metadataOverride,
      _filterValues,
      _sortRules,
      downloadFormat = "excel",
      onProgress,
    ) => {
      const rows = Array.isArray(dateRows) ? dateRows : [];
      if (!rows.length) {
        showToast("info", "No time card records are available for export.");
        return;
      }

      const groups = [];
      for (let index = 0; index < rows.length; index += 1) {
        const row = rows[index];
        const detailRows = await ensureExpandedRowsForDate(row?.date_key);
        groups.push({ summaryRow: row, detailRows });
        if (typeof onProgress === "function") {
          onProgress(Math.round(((index + 1) / rows.length) * 100));
        }
      }

      if (!groups.some((group) => group.detailRows.length)) {
        showToast("info", "No time card records are available for export.");
        return;
      }

      const exportToFormat =
        downloadFormat === "pdf"
          ? downloadGroupedPdf
          : downloadFormat === "csv"
            ? downloadGroupedCsv
            : downloadGroupedExcel;
      await exportToFormat(
        groups,
        timeCardDateWiseHeaders,
        timeCardDateWiseDetailHeaders,
        "Time Card Detailed",
        { ...metadata, "Total Date Count": rows.length },
      );
    },
    [
      downloadGroupedCsv,
      downloadGroupedExcel,
      downloadGroupedPdf,
      ensureExpandedRowsForDate,
      metadata,
    ],
  );

  const handleToggleEmployee = useCallback(
    async (row) => {
      const employeeId = row?.user_id || row?.id;
      if (!employeeId) return;

      const isExpanded =
        expandMode === "expand-all" || expandedEmployeeIds.includes(employeeId);

      if (isExpanded) {
        setExpandMode("collapse-all");
        setExpandedEmployeeIds((prev) =>
          prev.filter((id) => `${id}` !== `${employeeId}`),
        );
        if (`${expandedEmployeeId}` === `${employeeId}`) {
          setExpandedEmployeeId(null);
        }
        return;
      }

      setExpandMode("collapse-all");
      setExpandedEmployeeId(employeeId);
      setExpandedEmployeeIds((prev) =>
        prev.includes(employeeId) ? prev : [...prev, employeeId],
      );
      await ensureExpandedRowsForEmployee(employeeId);
    },
    [
      expandMode,
      expandedEmployeeId,
      expandedEmployeeIds,
      ensureExpandedRowsForEmployee,
    ],
  );

  const handleToggleDate = useCallback(
    async (row) => {
      const dateKey = row?.date_key;
      if (!dateKey) return;
      if (expandedDateKeys.includes(dateKey)) {
        setExpandedDateKeys((prev) => prev.filter((key) => key !== dateKey));
        return;
      }
      setExpandedDateKeys((prev) => [...prev, dateKey]);
      await ensureExpandedRowsForDate(dateKey);
    },
    [ensureExpandedRowsForDate, expandedDateKeys],
  );

  const paginatedRows = visibleRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  useEffect(() => {
    if (isDateWiseView || expandMode !== "expand-all") return;

    const nextExpandedIds = paginatedRows
      .map((row) => row?.user_id || row?.id)
      .filter(Boolean);
    setExpandedEmployeeIds(nextExpandedIds);
    nextExpandedIds.forEach((employeeId) => {
      ensureExpandedRowsForEmployee(employeeId);
    });
  }, [
    ensureExpandedRowsForEmployee,
    expandMode,
    isDateWiseView,
    paginatedRows,
  ]);

  const updateExpandMode = useCallback((event, data) => {
    const nextMode = data?.id;
    if (!nextMode) return;

    setExpandMode(nextMode);
    setExpandedEmployeeId(null);
    if (nextMode === "collapse-all") {
      setExpandedEmployeeIds([]);
    }
  }, []);

  const expandControl = useMemo(
    () => (
      <CompactHeaderSelectFilter
        dataList={EXPAND_TOGGLE_OPTIONS}
        selectedValue={
          EXPAND_TOGGLE_OPTIONS.find((option) => option.id === expandMode) ||
          EXPAND_TOGGLE_OPTIONS[0]
        }
        updateDataFunction={updateExpandMode}
        staticLabel=""
        widthSx={{
          width: { xs: "100%", sm: "auto" },
          minWidth: { xs: "100%", sm: 140 },
          maxWidth: { xs: "100%", sm: 140 },
        }}
        buttonSx={{
          minHeight: 40,
          height: 40,
          px: 1,
          borderRadius: 1.5,
          backgroundColor: designColors.white,
          border: `1px solid ${designColors.slate200}`,
          boxShadow: "none",
          color: designColors.slate700,
          "&:hover": {
            backgroundColor: designColors.slateBg,
            border: `1px solid ${designColors.slate300}`,
            boxShadow: "none",
          },
        }}
      />
    ),
    [expandMode, updateExpandMode],
  );

  const renderTimeCardDetailCell = (key, row) => {
    switch (key) {
      case "attendance_date":
        return row?.attendance_date || "-";
      case "in_time":
        return row?.in_time || "-";
      case "out_time":
        return row?.out_time || "-";
      case "total_hours":
        return row?.total_hours || "-";
      case "status_display":
        return (
          <Mui.Chip
            label={row?.status_display || "-"}
            size="small"
            variant="outlined"
            sx={{
              backgroundColor: row?.status_color || designColors.slate500,
              color: designColors.white,
              fontWeight: 600,
              minWidth: 92,
              height: 20,
              fontSize: "0.7rem",
            }}
          />
        );
      default:
        return row?.[key] || "-";
    }
  };

  const hasInTimeColumn = expandVisibleHeaders.some(
    (header) => header.key === "in_time",
  );
  const hasOutTimeColumn = expandVisibleHeaders.some(
    (header) => header.key === "out_time",
  );
  const inTimeIndex = expandVisibleHeaders.findIndex(
    (header) => header.key === "in_time",
  );
  const outTimeIndex = expandVisibleHeaders.findIndex(
    (header) => header.key === "out_time",
  );
  const shiftTimeGroupStartIndex =
    hasInTimeColumn &&
    hasOutTimeColumn &&
    Math.abs(inTimeIndex - outTimeIndex) === 1
      ? Math.min(inTimeIndex, outTimeIndex)
      : -1;
  const showShiftTimeGroupHeader = shiftTimeGroupStartIndex >= 0;
  const orderedDetailHeaders = expandVisibleHeaders;

  // Tighter than the shared tableRowSx (used app-wide) — scoped to just this
  // expand panel so the day-level rows sit closer together.
  const detailTableRowSx = {
    "& .MuiTableCell-root": {
      height: "8px",
      padding: "2px 10px",
    },
  };

  const renderDetailTable = (detailRows) => (
    <ApproveTable sx={timeCardDetailTableSx}>
      <Mui.TableHead>
        <Mui.TableRow>
          {expandVisibleHeaders.length === 0 ? (
            <Mui.TableCell align="center">Details</Mui.TableCell>
          ) : showShiftTimeGroupHeader ? (
            <>
              {orderedDetailHeaders.map((header, index) => {
                if (index === shiftTimeGroupStartIndex) {
                  return (
                    <Mui.TableCell
                      key="shift-time-group"
                      align="center"
                      colSpan={2}
                    >
                      Shift Time
                    </Mui.TableCell>
                  );
                }

                if (index === shiftTimeGroupStartIndex + 1) {
                  return null;
                }

                return (
                  <Mui.TableCell key={header.key} align="center" rowSpan={2}>
                    {header.name}
                  </Mui.TableCell>
                );
              })}
            </>
          ) : (
            orderedDetailHeaders.map((header) => (
              <Mui.TableCell key={header.key} align="center">
                {header.name}
              </Mui.TableCell>
            ))
          )}
        </Mui.TableRow>
        {showShiftTimeGroupHeader ? (
          <Mui.TableRow>
            {orderedDetailHeaders
              .slice(shiftTimeGroupStartIndex, shiftTimeGroupStartIndex + 2)
              .map((header) => (
                <Mui.TableCell key={header.key} align="center">
                  {header.name}
                </Mui.TableCell>
              ))}
          </Mui.TableRow>
        ) : null}
      </Mui.TableHead>
      <Mui.TableBody>
        {expandVisibleHeaders.length === 0 ? (
          <Mui.TableRow>
            <Mui.TableCell align="center">
              No detail columns selected.
            </Mui.TableCell>
          </Mui.TableRow>
        ) : detailRows.length === 0 ? (
          <TableEmptyState dataLength={detailRows} />
        ) : (
          detailRows.map((row) => (
            <Mui.TableRow
              key={`${row.raw_date}-${row.id || row.attendance_date}`}
            >
              {orderedDetailHeaders.map((header) => (
                <Mui.TableCell
                  key={`${row.id || row.attendance_date}-${header.key}`}
                  align="center"
                >
                  {renderTimeCardDetailCell(header.key, row)}
                </Mui.TableCell>
              ))}
            </Mui.TableRow>
          ))
        )}
      </Mui.TableBody>
    </ApproveTable>
  );

  const renderTimeCardIdentityCell = (key, row) => {
    switch (key) {
      case "employee_name": {
        const employeeId = row?.user_id || row?.id;
        const isExpanded = `${expandedEmployeeId}` === `${employeeId}`;
        return (
          <Mui.Stack direction="row" spacing={1} alignItems="center">
            <Mui.Avatar
              sx={{ width: 28, height: 28 }}
              alt={row?.employee_name?.charAt(0)?.toUpperCase()}
              src={row?.image_url ?? optionalAvathar}
            />
            <Mui.Typography
              sx={{
                fontWeight: isExpanded ? 700 : 500,
                color: designColors.slate900,
                fontSize: 14,
              }}
            >
              {row?.employee_name || "-"}
            </Mui.Typography>
          </Mui.Stack>
        );
      }
      case "employee_id":
        return row?.employee_id || "-";
      case "department_name":
        return row?.department_name || "-";
      case "total_working_hours":
        return row?.total_working_hours || "00:00";
      default:
        return null;
    }
  };

  const renderDateWiseDetailTable = (detailRows, rowKey) => {
    const search = dateWiseDetailSearchByKey[rowKey] || "";
    const normalizedSearch = search.trim().toLowerCase();
    const filteredDetailRows = normalizedSearch
      ? detailRows.filter((row) => {
          const name = `${row?.employee_name || ""}`.toLowerCase();
          const employeeId = `${row?.employee_id || ""}`.toLowerCase();
          return (
            name.includes(normalizedSearch) ||
            employeeId.includes(normalizedSearch)
          );
        })
      : detailRows;
    const detailPage = dateWiseDetailPageByKey[rowKey] || 0;
    const pagedDetailRows = filteredDetailRows.slice(
      detailPage * DATE_WISE_DETAIL_PAGE_SIZE,
      detailPage * DATE_WISE_DETAIL_PAGE_SIZE + DATE_WISE_DETAIL_PAGE_SIZE,
    );

    return (
      <>
        {detailRows.length > 0 ? (
          <Mui.Box
            sx={{
              p: 1.25,
              pb: 0.75,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Mui.TextField
              size="small"
              sx={{ width: 280, maxWidth: "100%" }}
              placeholder="Search by name, id..."
              value={search}
              onChange={(event) => {
                const nextValue = event.target.value;
                setDateWiseDetailSearchByKey((prev) => ({
                  ...prev,
                  [rowKey]: nextValue,
                }));
                setDateWiseDetailPageByKey((prev) => ({
                  ...prev,
                  [rowKey]: 0,
                }));
              }}
              InputProps={{
                startAdornment: (
                  <Mui.InputAdornment position="start">
                    <SearchRounded
                      sx={{ fontSize: 18, color: designColors.slate500 }}
                    />
                  </Mui.InputAdornment>
                ),
              }}
            />
          </Mui.Box>
        ) : null}
        <ApproveTable sx={timeCardDetailTableSx}>
          <Mui.TableHead>
            <Mui.TableRow>
              {timeCardDateWiseDetailHeaders.map((header) => (
                <Mui.TableCell
                  key={header.key}
                  align={header.align || "center"}
                >
                  {header.name}
                </Mui.TableCell>
              ))}
            </Mui.TableRow>
          </Mui.TableHead>
          <Mui.TableBody>
            {detailRows.length === 0 || filteredDetailRows.length === 0 ? (
              <TableEmptyState dataLength={detailRows} />
            ) : (
              pagedDetailRows.map((row, index) => (
                <Mui.TableRow
                  key={`${row.employee_id}-${row.raw_date}-${index}`}
                >
                  {timeCardDateWiseDetailHeaders.map((header) => (
                    <Mui.TableCell
                      key={header.key}
                      align={header.align || "center"}
                    >
                      {header.key === "status_display"
                        ? row?.status_display || "-"
                        : row?.[header.key] || "-"}
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              ))
            )}
          </Mui.TableBody>
        </ApproveTable>
        {compactRowPager({
          page: detailPage,
          pageSize: DATE_WISE_DETAIL_PAGE_SIZE,
          total: filteredDetailRows.length,
          itemLabel: "employees",
          onPageChange: (nextPage) =>
            setDateWiseDetailPageByKey((prev) => ({
              ...prev,
              [rowKey]: nextPage,
            })),
        })}
      </>
    );
  };

  return (
    <ThemeProvider theme={withDesignFont(theme)}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        {/* Single page-level scroll, matching the Working Hours Summary/
            Detail reports right above this one in the same tab group
            (WorkingHours/index.js) — a sticky filter bar under the fixed
            AppBar, a table head pinned under that, and a footer pinned to
            the bottom, instead of this report's own separate maxHeight
            scroll box. */}
        <Box sx={{ pb: 0, width: "100%", maxWidth: "100%" }}>
          <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
            <CustomFilter
              key={`time-card-filter-${viewMode?.id || "employee-wise"}-${startDate}-${endDate}`}
              title="Time card"
              titleOverride={titleOverride}
              filterHeaders={activeHeaders}
              data={activeBaseRows}
              filteredData={visibleRows}
              filteredDataReady={filteredRows !== null && filteredRowsReady}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={screenAccess}
              isAttendanceScreenReport={true}
              showDownloadOptions={true}
              showDownloadTypeSelector={true}
              downloadOptionStates={{
                summarized: {
                  disabled: !visibleRows.length,
                  reason: "No time card data is available to export.",
                },
                detailed: {
                  disabled: !visibleRows.length,
                  reason: "No time card data is available to export.",
                },
              }}
              downloadDisabledReason="No time card data is available to export."
              showtitle_count={true}
              hideCount={true}
              includeMetadata={true}
              metadata={metadata}
              customSummarizedDownloadHandler={handleSummarizedDownload}
              customDetailedDownloadHandler={
                isDateWiseView
                  ? handleDateWiseDetailedDownload
                  : handleDownloadReport
              }
              downloadFormats={["excel", "pdf", "csv"]}
              pdfRowCountOverride={timeCardPdfRowCount}
              labeledToolbar={true}
              searchPlaceholder="Search by employee, department..."
              newButtonComponent={isDateWiseView ? null : expandControl}
              toolbarActions={bulkTimeCardAction}
            />
          </Box>

          <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
            <Mui.Box>
              <Mui.TableContainer
                sx={{
                  boxSizing: "border-box",
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: { xs: "auto", sm: "visible" },
                  overflowY: { xs: "auto", sm: "visible" },
                  pb: footerHeight ? `${footerHeight}px` : 0,
                  borderLeft: `1px solid ${designColors.slate200}`,
                  borderRight: `1px solid ${designColors.slate200}`,
                  borderBottom: `1px solid ${designColors.slate200}`,
                }}
              >
                <ApproveTable stickyHeader sx={{ width: "100%" }}>
                  <Mui.TableHead sx={designShell.tableStickyHead}>
                    <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                      <Mui.TableCell
                        sx={{
                          ...approveTableHeadCell,
                          top: APP_BAR_OFFSET + customFilterHeight,
                          width: 56,
                          borderTopLeftRadius: 0,
                        }}
                      />
                      {activeHeaders.map((header, headerIndex) => (
                        <Mui.TableCell
                          key={header.key}
                          sx={{
                            ...approveTableHeadCell,
                            top: APP_BAR_OFFSET + customFilterHeight,
                            ...(headerIndex === activeHeaders.length - 1
                              ? { borderTopRightRadius: 0 }
                              : {}),
                          }}
                        >
                          {header.name}
                        </Mui.TableCell>
                      ))}
                    </Mui.TableRow>
                  </Mui.TableHead>
                  <Mui.TableBody>
                    {visibleRows.length === 0 ? (
                      <TableEmptyState dataLength={activeBaseRows} />
                    ) : (
                      paginatedRows.map((row) => {
                        const employeeId = row?.user_id || row?.id;
                        const rowKey = isDateWiseView
                          ? row?.date_key
                          : employeeId;
                        const isExpanded = isDateWiseView
                          ? expandedDateKeys.includes(rowKey)
                          : expandMode === "expand-all" ||
                            expandedEmployeeIds.includes(employeeId);
                        const detailRows = isDateWiseView
                          ? dateDetailsByKey[rowKey] || []
                          : expandedRowsByEmployee[employeeId] || [];
                        const isLoading = isDateWiseView
                          ? loadingDateKeys.includes(rowKey)
                          : loadingEmployeeIds.includes(employeeId);

                        return (
                          <React.Fragment key={`${rowKey || row?.employee_id}`}>
                            <Mui.TableRow
                              hover
                              sx={{
                                ...designShell.tableRowHover,
                                "&:last-child td": { borderBottom: "none" },
                                "& td": { backgroundColor: designColors.white },
                              }}
                            >
                              <Mui.TableCell
                                sx={approveTableBodyCell}
                                onClick={() =>
                                  isDateWiseView
                                    ? handleToggleDate(row)
                                    : handleToggleEmployee(row)
                                }
                              >
                                <ArrowRightIcon
                                  sx={{
                                    color: designColors.slate500,
                                    cursor: "pointer",
                                    transition: "transform 0.2s ease",
                                    transform: isExpanded
                                      ? "rotate(90deg)"
                                      : "rotate(0deg)",
                                  }}
                                />
                              </Mui.TableCell>
                              {activeHeaders.map((header) => (
                                <Mui.TableCell
                                  key={header.key}
                                  sx={approveTableBodyCell}
                                >
                                  {isDateWiseView
                                    ? header.key === "date"
                                      ? formatAttendanceDate(row?.date)
                                      : row?.[header.key] || "-"
                                    : renderTimeCardIdentityCell(
                                        header.key,
                                        row,
                                      )}
                                </Mui.TableCell>
                              ))}
                            </Mui.TableRow>

                            {isExpanded ? (
                              <Mui.TableRow
                                sx={{
                                  "& td": {
                                    backgroundColor: designColors.white,
                                  },
                                }}
                              >
                                <Mui.TableCell
                                  colSpan={1 + activeHeaders.length}
                                >
                                  {isLoading ? (
                                    <Mui.Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        py: 3,
                                      }}
                                    >
                                      <Mui.CircularProgress
                                        size={28}
                                        sx={{ color: designColors.teal }}
                                      />
                                    </Mui.Box>
                                  ) : (
                                    <Mui.TableContainer
                                      sx={timeCardDetailTableContainerSx}
                                    >
                                      {isDateWiseView
                                        ? renderDateWiseDetailTable(
                                            detailRows,
                                            rowKey,
                                          )
                                        : renderDetailTable(detailRows)}
                                    </Mui.TableContainer>
                                  )}
                                </Mui.TableCell>
                              </Mui.TableRow>
                            ) : null}
                          </React.Fragment>
                        );
                      })
                    )}
                  </Mui.TableBody>
                </ApproveTable>
              </Mui.TableContainer>
            </Mui.Box>

            <Mui.Box ref={footerRef} sx={approveStickyFooterBar}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: visibleRows.length,
                itemLabel: "records",
                onPageChange: setLeavePage,
                onRowsPerPageChange: handleChangeRowsPerPage,
                rowsPerPageOptions: isDateWiseView
                  ? DATE_WISE_ROWS_PER_PAGE_OPTIONS
                  : rowsPerPageOptions,
              })}
            </Mui.Box>
          </Box>
        </Box>
      </FilterProvider>
    </ThemeProvider>
  );
};
